import { useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { tokens } from "../../theme";

import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LocalGroceryStoreOutlinedIcon from '@mui/icons-material/LocalGroceryStoreOutlined';
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BlurOnOutlinedIcon from "@mui/icons-material/BlurOnOutlined";
import PieChartOutlinedIcon from "@mui/icons-material/PieChartOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import logo from "../../imgs/logo.png";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AddCardIcon from '@mui/icons-material/AddCard';
import CleanHandsIcon from '@mui/icons-material/CleanHands';
import InventoryIcon from '@mui/icons-material/Inventory';

const Item = ({ title, to, icon, selected, setSelected, isCollapsed }) => {
  const active = selected === title;

  return (
    <Link to={to} style={{ textDecoration: "none" }}>
      <Box
        onClick={() => setSelected(title)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: isCollapsed ? "12px" : "12px 16px",
          margin: "6px 10px",
          borderRadius: "10px",
          cursor: "pointer",
          backgroundColor: active ? tokens.redAccent[400] : "transparent",
          color: active ?  tokens.grey[100] : tokens.blueAccent[300],
          transition: "all 0.2s ease",

          "&:hover": {
            backgroundColor: tokens.grey[200],
            color: tokens.redAccent[400],
          },
        }}
      >
        {icon}

        {!isCollapsed && (
          <Typography fontSize="13px" fontWeight="500">
            {title}
          </Typography>
        )}
      </Box>
    </Link>
  );
};
const Section = ({ title, icon, children, isCollapsed }) => {

  const [open, setOpen] = useState(false);

  return (
    <Box>

      {/* Section Header */}
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isCollapsed ? "12px" : "12px 16px",
          margin: "6px 10px",
          borderRadius: "10px",
          cursor: "pointer",
          color: tokens.blueAccent[400],
          "&:hover": {
            backgroundColor: tokens.grey[200],
          },
        }}
      >

        {/* LEFT SIDE (ICON + TITLE) */}
        <Box display="flex" alignItems="center" gap="12px">
          {icon}

          {!isCollapsed && (
            <Typography fontSize="13px" fontWeight="600">
              {title}
            </Typography>
          )}
        </Box>

        {/* RIGHT SIDE (ARROW ICON) */}
        {!isCollapsed && (
          open ? <KeyboardArrowUpIcon fontSize="15px"/> : <KeyboardArrowDownIcon fontSize="15px"/>
        )}

      </Box>

      {/* DROPDOWN ITEMS */}
      {open && !isCollapsed && (
        <Box sx={{ marginLeft: "25px"}}>
          {children}
        </Box>
      )}

    </Box>
  );
};

const SideBar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selected, setSelected] = useState("Dashboard");

  return (
    <Box
      sx={{
        width: isCollapsed ? "80px" : "250px",
        height: "100vh",
        overflow: "auto",
        "&::-webkit-scrollbar": { width: "6px" },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "#9ca3af", borderRadius: "10px" },
        backgroundColor: "#fff",
        borderRight: "1.5px solid #e5e7eb",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* TOP AREA */}
      <Box>
         
        {/* LOGO */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isCollapsed ? "center" : "space-between",
            padding: "5px",
            borderBottom: "1.5px solid #e5e7eb",
          }}
        >
          {!isCollapsed && (
            <img src={logo} width="150px" alt="logo" />
          )}

          <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
            <MenuOutlinedIcon />
          </IconButton>
        </Box>

        {/* MENU ITEMS */}

        <Box mt="20px" backgroundColor={tokens.grey[100]}>

  <Item
    title="Dashboard"
    to="/dashboard"
    icon={<HomeOutlinedIcon />}
    selected={selected}
    setSelected={setSelected}
    isCollapsed={isCollapsed}
  />

  {/* INVENTORY */}
  <Section title="Stock" icon={<LocalGroceryStoreOutlinedIcon />} isCollapsed={isCollapsed}>

    <Item
      title="Products"
      to="/products"
      icon={<InventoryIcon />}
      selected={selected}
      setSelected={setSelected}
      isCollapsed={isCollapsed}
    />

    <Item
      title="Stock Ledger"
      to="/stockledger"
      icon={<TimelineOutlinedIcon />}
      selected={selected}
      setSelected={setSelected}
      isCollapsed={isCollapsed}
    />

  </Section>
  <Section title="Suppliers & Customers" icon={<PersonOutlinedIcon />} isCollapsed={isCollapsed}>

    <Item
      title="Suppliers"
      to="/suppliers"
      icon={<PersonOutlinedIcon />}
      selected={selected}
      setSelected={setSelected}
      isCollapsed={isCollapsed}
    />

    <Item
      title="Customers"
      to="/customers"
      icon={<PersonOutlinedIcon />}
      selected={selected}
      setSelected={setSelected}
      isCollapsed={isCollapsed}
    />

  </Section>

  {/* SALES */}
  <Section title="Sales" icon={<TimelineOutlinedIcon />} isCollapsed={isCollapsed}>

    <Item
      title="Manage Sales"
      to="/sales"
      icon={<PieChartOutlinedIcon />}
      selected={selected}
      setSelected={setSelected}
      isCollapsed={isCollapsed}
    />

    <Item
      title="Add Sale"
      to="/addsale"
      icon={<CalendarTodayOutlinedIcon />}
      selected={selected}
      setSelected={setSelected}
      isCollapsed={isCollapsed}
    />

  </Section>

  {/* PURCHASES */}
  <Section title="Purchases" icon={<BarChartOutlinedIcon />} isCollapsed={isCollapsed}>

    <Item
      title="Manage Purchase"
      to="/purchases"
      icon={<PieChartOutlinedIcon />}
      selected={selected}
      setSelected={setSelected}
      isCollapsed={isCollapsed}
    />

    <Item
      title="Add Purchase"
      to="/addpurchase"
      icon={<CalendarTodayOutlinedIcon />}
      selected={selected}
      setSelected={setSelected}
      isCollapsed={isCollapsed}
    />

  </Section>

  {/* ACCOUNTING */}
  <Section title="Accounting" icon={<AccountBalanceIcon />} isCollapsed={isCollapsed}>

    <Item
      title="Expenses"
      to="/expenses"
      icon={<AddCardIcon />}
      selected={selected}
      setSelected={setSelected}
      isCollapsed={isCollapsed}
    />

    <Item
      title="Services"
      to="/services"
      icon={<CleanHandsIcon />}
      selected={selected}
      setSelected={setSelected}
      isCollapsed={isCollapsed}
    />

  </Section>

  {/* DEBTS */}
  <Section title="Debts" icon={<CreditScoreIcon />} isCollapsed={isCollapsed}>

    <Item
      title="Customer Debts"
      to="/customerdebts"
      icon={<PersonRemoveIcon />}
      selected={selected}
      setSelected={setSelected}
      isCollapsed={isCollapsed}
    />

  </Section>

</Box>
      </Box>

      {/* BOTTOM SPACE (optional logout area later) */}
      <Box mb="20px" />
    </Box>
  );
};

export default SideBar;